import { Controller } from "@hotwired/stimulus"

// Charge le détail JSON Postmark à l’ouverture d’un <details> (RailsAdmin ; réservé admins côté serveur).
export default class extends Controller {
  static values = {
    url: String,
    labels: Object
  }

  static targets = ["panel", "status"]

  connect() {
    this.loaded = false
    this.blobUrls = []
    this.boundToggle = this.onToggle.bind(this)
    this.element.addEventListener("toggle", this.boundToggle)
  }

  disconnect() {
    this.element.removeEventListener("toggle", this.boundToggle)
    this.revokeBlobs()
  }

  onToggle() {
    if (!this.element.open || this.loaded) return
    this.load()
  }

  async load() {
    this.loaded = true
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.statusTarget.dataset.loadingText || "…"
    }
    if (this.hasPanelTarget) {
      this.panelTarget.innerHTML = ""
    }

    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      const data = await response.json()
      if (!response.ok || !data.ok) {
        throw new Error(data.error || response.statusText)
      }
      this.renderDetails(data.details)
    } catch (e) {
      if (this.hasPanelTarget) {
        this.panelTarget.innerHTML = `<p class="text-danger mb-0">${this.escapeHtml(e.message)}</p>`
      }
    } finally {
      if (this.hasStatusTarget) {
        this.statusTarget.textContent = ""
      }
    }
  }

  renderDetails(d) {
    if (!this.hasPanelTarget) return

    this.revokeBlobs()

    const blocks = []

    const mainRows = []
    const midSlug = this.slugForDomId(d.MessageID || d.postmark_message_id || d.message_id || "msg")
    const postmarkMessageId = d.postmark_message_id || d.MessageID || d.message_id
    this.addTextRow(mainRows, "message_id", postmarkMessageId, `postmark-outbound-detail-${midSlug}-message-id`, { asCode: true })
    this.addTextRow(mainRows, "from", d.From, `postmark-outbound-detail-${midSlug}-from`)
    this.addTextRow(mainRows, "subject", d.Subject, `postmark-outbound-detail-${midSlug}-subject`)
    this.addTextRow(mainRows, "sent_at", this.formatTimestamp(d.ReceivedAt), `postmark-outbound-detail-${midSlug}-sent-at`)
    this.addTextRow(mainRows, "status", this.translateMessageStatus(d.Status), `postmark-outbound-detail-${midSlug}-status`)
    this.addTextRow(mainRows, "tag", d.Tag, `postmark-outbound-detail-${midSlug}-tag`)
    const recipients = this.formatList(d.Recipients || d.To)
    this.addTextRow(mainRows, "recipients", recipients, `postmark-outbound-detail-${midSlug}-recipients`)
    this.addAttachmentsRow(mainRows, d, midSlug)
    if (mainRows.length) blocks.push(this.wrapTable(mainRows, `postmark-outbound-detail-${midSlug}-main`))

    blocks.push(this.renderDeliveryInsights(d.MessageEvents || d.message_events, midSlug))

    if (d.Metadata && typeof d.Metadata === "object" && Object.keys(d.Metadata).length) {
      const metaRows = [
        this.sectionRow(this.label("metadata")),
        `<tr id="postmark-outbound-detail-${midSlug}-metadata"><td colspan="2" class="small py-1"><pre class="small mb-0 bg-light rounded p-2" style="max-height:200px;overflow:auto">${this.escapeHtml(JSON.stringify(d.Metadata, null, 2))}</pre></td></tr>`
      ]
      blocks.push(this.wrapTable(metaRows, `postmark-outbound-detail-${midSlug}-metadata-table`))
    }

    if (d.TextBody) {
      blocks.push(
        this.wrapTable(
          [
            this.sectionRow(this.label("text_body")),
            `<tr id="postmark-outbound-detail-${midSlug}-text-body"><td colspan="2" class="small py-1"><pre class="small mb-0 border rounded p-2" style="white-space:pre-wrap;max-height:240px;overflow:auto">${this.escapeHtml(d.TextBody)}</pre></td></tr>`
          ],
          `postmark-outbound-detail-${midSlug}-text`
        )
      )
    }

    if (d.HtmlBody) {
      blocks.push(
        this.wrapTable(
          [
            this.sectionRow(this.label("html_body")),
            `<tr id="postmark-outbound-detail-${midSlug}-html-body"><td colspan="2" class="small py-1"><iframe class="w-100 border rounded postmark-html-preview" title="HTML" style="height:280px" sandbox="allow-same-origin" src="about:blank"></iframe></td></tr>`
          ],
          `postmark-outbound-detail-${midSlug}-html`
        )
      )
    }

    const arIdMarkers = Array.isArray(d.PostmarkOutboundMessageRecordIds)
      ? d.PostmarkOutboundMessageRecordIds
          .map(
            (rid) =>
              `<span id="postmark-outbound-message-ar-${this.escapeHtml(String(rid))}" class="d-none" aria-hidden="true"></span>`
          )
          .join("")
      : ""

    this.panelTarget.innerHTML = arIdMarkers + blocks.filter(Boolean).join("")

    if (d.HtmlBody) {
      const iframe = this.panelTarget.querySelector("iframe.postmark-html-preview")
      if (iframe) {
        const doc = `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank" rel="noopener"></head><body>${d.HtmlBody}</body></html>`
        const url = URL.createObjectURL(new Blob([doc], { type: "text/html" }))
        this.blobUrls.push(url)
        iframe.src = url
      }
    }
  }

  slugForDomId(raw) {
    return String(raw || "x")
      .replace(/[^a-zA-Z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || "x"
  }

  wrapTable(bodyRows, tableId) {
    if (!bodyRows.length) return ""
    const idAttr = tableId ? ` id="${this.escapeHtml(tableId)}"` : ""
    return `<div class="table-responsive mb-2"><table${idAttr} class="table table-sm table-borderless mb-0 postmark-outbound-detail"><tbody>${bodyRows.join("")}</tbody></table></div>`
  }

  sectionRow(title) {
    return `<tr class="postmark-outbound-detail-section"><td colspan="2" class="small text-muted fw-semibold border-top pt-2 pb-1">${this.escapeHtml(title)}</td></tr>`
  }

  addTextRow(rows, labelKey, value, rowId, opts = {}) {
    if (value === undefined || value === null || value === "") return
    const label = this.escapeHtml(this.label(labelKey))
    const escaped = this.escapeHtml(String(value))
    const cell = opts.asCode
      ? `<code class="postmark-outbound-message-id user-select-all">${escaped}</code>`
      : escaped
    const idAttr = rowId ? ` id="${this.escapeHtml(rowId)}"` : ""
    rows.push(
      `<tr${idAttr} class="postmark-outbound-detail-event"><th scope="row" class="small text-muted fw-normal align-top py-1 pe-2" style="width:7.5rem">${label}</th><td class="small py-1">${cell}</td></tr>`
    )
  }

  addHtmlRow(rows, labelKey, html, rowId) {
    if (!html) return
    const label = this.escapeHtml(this.label(labelKey))
    const idAttr = rowId ? ` id="${this.escapeHtml(rowId)}"` : ""
    rows.push(
      `<tr${idAttr} class="postmark-outbound-detail-event"><th scope="row" class="small text-muted fw-normal align-top py-1 pe-2" style="width:7.5rem">${label}</th><td class="small py-1">${html}</td></tr>`
    )
  }

  formatAttachmentsCell(d) {
    const parts = []
    const stored = Array.isArray(d.StoredAttachments) ? d.StoredAttachments : []
    const linkedNames = new Set(
      stored.map((a) => this.normalizeAttachmentName(a.filename)).filter(Boolean)
    )
    stored.forEach((a) => {
      const url = this.escapeHtml(String(a.url || ""))
      const fn = this.escapeHtml(String(a.filename || "file"))
      if (url) parts.push(`<a href="${url}" rel="noopener">${fn}</a>`)
    })
    const postmark = Array.isArray(d.Attachments) ? d.Attachments : []
    const extraNames = postmark
      .filter(Boolean)
      .map((x) => String(x).trim())
      .filter((name) => !linkedNames.has(this.normalizeAttachmentName(name)))
    if (extraNames.length) {
      if (parts.length) parts.push(`<span class="text-muted mx-1">·</span>`)
      parts.push(`<span class="text-muted">${extraNames.map((n) => this.escapeHtml(n)).join(", ")}</span>`)
    }
    return parts.length ? parts.join(" ") : ""
  }

  normalizeAttachmentName(name) {
    return String(name || "")
      .trim()
      .split(/[/\\]/)
      .pop()
      .toLowerCase()
  }

  addAttachmentsRow(rows, d, midSlug) {
    const html = this.formatAttachmentsCell(d)
    if (!html) return
    this.addHtmlRow(rows, "attachments", html, `postmark-outbound-detail-${midSlug}-attachments`)
  }

  revokeBlobs() {
    this.blobUrls.forEach((u) => URL.revokeObjectURL(u))
    this.blobUrls = []
  }

  messageEventRawDetail(ev) {
    if (typeof ev.Details === "string") return ev.Details
    const d = ev.Details || ev.details || {}
    if (typeof d === "string") return d
    return (
      d.Summary ||
      d.summary ||
      d.DeliveryMessage ||
      d.deliveryMessage ||
      d.Content ||
      d.content ||
      d.Message ||
      d.message ||
      ""
    )
  }

  eventReceivedAtRaw(ev) {
    return ev.ReceivedAt || ev.received_at || ev.ReceivedAtUtc || ""
  }

  eventReceivedAtMs(ev) {
    const raw = this.eventReceivedAtRaw(ev)
    const ms = Date.parse(raw)
    return Number.isNaN(ms) ? 0 : ms
  }

  // Neutralise les segments variables des réponses SMTP (421 4.1.0 <token>, crochets, UUID…)
  // pour regrouper les tentatives « Transient » identiques.
  normalizeDetailForFingerprint(detail) {
    let s = String(detail || "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase()
    s = s.replace(/\[[^\]]*\]/g, "[]")
    s = s.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g, "<uuid>")
    // smtp;421 4.1.0 <session> … (token souvent sans crochets, ex. Orange)
    s = s.replace(/smtp;421\s+4\.1\.0\s+\S+/g, "smtp;421 4.1.0 <sid>")
    s = s.replace(/\b421\s+4\.1\.0\s+\S+/g, "421 4.1.0 <sid>")
    s = s.replace(/\b4\.1\.0\s+\S+/g, "4.1.0 <sid>")
    return s.trim()
  }

  eventFingerprint(ev) {
    const type = String(ev.Type || ev.type || "")
      .trim()
      .toLowerCase()
    const recipient = String(ev.Recipient || ev.recipient || "")
      .trim()
      .toLowerCase()
    const det = this.normalizeDetailForFingerprint(this.messageEventRawDetail(ev))
    return `${type}|${recipient}|${det}`
  }

  // Regroupe toutes les occurrences d’une même empreinte (pas seulement consécutives),
  // en conservant l’ordre d’apparition des premières occurrences sur la timeline.
  buildEventDisplayGroups(sorted) {
    const seen = new Set()
    const groups = []
    sorted.forEach((ev) => {
      const fp = this.eventFingerprint(ev)
      if (seen.has(fp)) return
      seen.add(fp)
      groups.push(sorted.filter((e) => this.eventFingerprint(e) === fp))
    })
    return groups
  }

  renderDeliveryInsights(events, midSlug) {
    const title = this.label("events_title")
    const rows = [this.sectionRow(title)]

    let alerts = ""

    if (!Array.isArray(events) || events.length === 0) {
      rows.push(
        `<tr id="postmark-outbound-detail-${midSlug}-events-empty"><td colspan="2" class="small text-muted py-1">${this.escapeHtml(this.label("events_empty"))}</td></tr>`
      )
    } else {
      const hasBounce = events.some((e) => (e.Type || e.type) === "Bounced")
      const hasTransient = events.some((e) => (e.Type || e.type) === "Transient")
      if (hasBounce) {
        alerts += `<div class="alert alert-danger small py-2 mb-2">${this.escapeHtml(this.label("bounce_explanation"))}</div>`
      }
      if (hasTransient) {
        alerts += `<div class="alert alert-warning small py-2 mb-2">${this.escapeHtml(this.label("transient_explanation"))}</div>`
      }

      const sorted = [...events].sort((a, b) => this.eventReceivedAtMs(a) - this.eventReceivedAtMs(b))

      const groups = this.buildEventDisplayGroups(sorted)
      let eventIdx = 0
      groups.forEach((group) => {
        if (group.length === 1) {
          rows.push(this.renderEventRow(group[0], `postmark-outbound-detail-${midSlug}-event-${eventIdx++}`))
        } else {
          const latest = group[group.length - 1]
          const older = group.slice(0, -1)
          rows.push(this.renderEventRow(latest, `postmark-outbound-detail-${midSlug}-event-${eventIdx++}`))
          rows.push(this.renderSimilarEventsCollapse(older, midSlug, eventIdx))
          eventIdx += older.length
        }
      })

      rows.push(
        `<tr><td colspan="2" class="small py-1"><details class="mb-0"><summary class="text-muted">${this.escapeHtml(this.label("events_raw_toggle"))}</summary><pre class="small bg-light p-2 rounded mt-1 mb-0" style="max-height:200px;overflow:auto">${this.escapeHtml(JSON.stringify(events, null, 2))}</pre></details></td></tr>`
      )
    }

    return `${alerts}${this.wrapTable(rows, `postmark-outbound-detail-${midSlug}-events`)}`
  }

  renderSimilarEventsCollapse(older, midSlug, startIdx) {
    const count = older.length
    const summary = this.escapeHtml(this.label("similar_events_more", { count }))
    const innerRows = older
      .map((ev, i) => this.renderEventRow(ev, `postmark-outbound-detail-${midSlug}-event-${startIdx + i}`))
      .join("")
    return `<tr class="postmark-outbound-similar-events"><td colspan="2" class="small py-1"><details class="mb-0"><summary class="text-muted">${summary}</summary><div class="mt-2 border rounded p-2 bg-light"><table class="table table-sm table-borderless mb-0 postmark-outbound-detail"><tbody>${innerRows}</tbody></table></div></details></td></tr>`
  }

  renderEventRow(ev, rowId) {
    const type = ev.Type || ev.type || ""
    const typeLabel = this.escapeHtml(this.eventTypeLabel(type))
    const badgeClass = this.eventBadgeClass(type)
    const when = this.escapeHtml(this.formatTimestamp(this.eventReceivedAtRaw(ev)))
    const left = `<span class="badge ${badgeClass}">${typeLabel}</span><div class="text-muted mt-1" style="font-size:0.8125rem">${when}</div>`
    const det = this.messageEventRawDetail(ev)
    const rec = ev.Recipient || ev.recipient
    const recipient = rec ? `<div class="mb-1">${this.escapeHtml(rec)}</div>` : ""
    const detailHtml = det
      ? `<div class="text-muted" style="white-space:pre-wrap;font-size:0.8125rem">${this.escapeHtml(det)}</div>`
      : ""
    const right = `${recipient}${detailHtml}`
    const idAttr = rowId ? ` id="${this.escapeHtml(rowId)}"` : ""
    return `<tr${idAttr} class="postmark-outbound-detail-event"><th scope="row" class="align-top small py-1 pe-2 fw-normal" style="width:7.5rem">${left}</th><td class="small py-1">${right || "—"}</td></tr>`
  }

  eventTypeLabel(type) {
    const t = String(type || "").trim()
    const keyByTypeLower = {
      delivered: "event_delivered",
      bounced: "event_bounced",
      transient: "event_transient",
      opened: "event_opened",
      linkclicked: "event_linkclicked",
      subscriptionchanged: "event_subscriptionchanged"
    }
    const labelKey = keyByTypeLower[t.replace(/\s+/g, "").toLowerCase()]
    if (labelKey) {
      const lbl = this.label(labelKey)
      if (lbl !== labelKey) return lbl
    }
    return type || "—"
  }

  eventBadgeClass(type) {
    const t = String(type || "").toLowerCase()
    switch (t) {
      case "bounced":
        return "bg-danger"
      case "transient":
        return "bg-warning"
      case "delivered":
        return "bg-success"
      case "opened":
        return "bg-info"
      case "linkclicked":
        return "bg-secondary"
      default:
        return "bg-light text-dark border"
    }
  }

  formatList(to) {
    if (!to) return ""
    if (typeof to === "string") return to
    if (Array.isArray(to)) {
      return to.map((x) => (typeof x === "object" && x.Email ? x.Email : String(x))).join(", ")
    }
    return String(to)
  }

  label(key, vars = {}) {
    let raw =
      this.hasLabelsValue && this.labelsValue && this.labelsValue[key] ? this.labelsValue[key] : key
    if (typeof raw !== "string") raw = String(raw)
    Object.entries(vars).forEach(([k, v]) => {
      raw = raw.split(`%{${k}}`).join(String(v))
    })
    return raw
  }

  translateMessageStatus(raw) {
    if (raw === undefined || raw === null || raw === "") return ""
    const s = String(raw).trim()
    if (s === "—") return s
    const k = `status_${s.toLowerCase()}`
    const lbl = this.label(k)
    return lbl !== k ? lbl : s
  }

  formatTimestamp(iso) {
    if (iso === undefined || iso === null || iso === "") return ""
    const raw = String(iso).trim()
    if (raw === "—") return raw
    const ms = Date.parse(raw)
    if (Number.isNaN(ms)) return raw
    let lang = (typeof document !== "undefined" && document.documentElement.lang) || "fr"
    if (lang.length === 2) lang = `${lang}-${lang.toUpperCase()}`
    try {
      return new Intl.DateTimeFormat(lang, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      }).format(new Date(ms))
    } catch {
      return raw
    }
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
